'use client';

import { useState } from 'react';
import { UtensilsCrossed, Clock, Users, Star, Armchair, Shirt } from 'lucide-react';
import { Badge } from '@/shared/ui';
import { cn } from '@/core/utils/cn';
import { DiningReservationDrawer } from './DiningReservationDrawer';
import type { Restaurant } from '../types/dining.types';
import { useDiningData } from '../hooks/useDining';
import { useTranslation } from '@/core/i18n';

const PRICE_LABELS: Record<number, string> = { 1: '$', 2: '$$', 3: '$$$' };

/* SVG restaurant illustration */
function RestaurantIllustration({ type, className }: { type: string; className?: string }) {
  const { t } = useTranslation();
  const color = type === 'Fine Dining' ? '#C8913A' : type === 'Casual Dining' ? '#0D9E99' : '#2563EB';
  return (
    <svg className={className} viewBox="0 0 280 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="280" height="140" rx="12" fill="#0D1523" />
      <circle cx="70" cy="70" r="18" fill={color} fillOpacity="0.08" stroke={color} strokeOpacity="0.2" />
      <circle cx="140" cy="60" r="22" fill={color} fillOpacity="0.08" stroke={color} strokeOpacity="0.2" />
      <circle cx="210" cy="75" r="16" fill={color} fillOpacity="0.08" stroke={color} strokeOpacity="0.2" />
      <circle cx="52" cy="70" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="88" cy="70" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="70" cy="52" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="70" cy="88" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="118" cy="60" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="162" cy="60" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="140" cy="38" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="140" cy="82" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="194" cy="75" r="4" fill={color} fillOpacity="0.15" />
      <circle cx="226" cy="75" r="4" fill={color} fillOpacity="0.15" />
      <line x1="135" y1="100" x2="135" y2="125" stroke={color} strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="140" y1="100" x2="140" y2="125" stroke={color} strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="145" y1="100" x2="145" y2="125" stroke={color} strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round" />
      <text x="140" y="135" textAnchor="middle" fill={color} fillOpacity="0.4" fontSize="9" fontFamily="sans-serif">{type}</text>
    </svg>
  );
}

/* Tables-available indicator — shows total capacity (no live availability API) */
function AvailabilityIndicator({ capacity }: { capacity: number }) {
  const { t } = useTranslation();
  const tablesEst = Math.max(Math.floor(capacity / 4), 0);
  return (
    <Badge id="dining-avail-badge" variant="default">
      {tablesEst} {t('dining.tablesAvailable')}
    </Badge>
  );
}

interface HotelDiningSectionProps {
  checkIn?: string;
  checkOut?: string;
  isHotelGuest?: boolean;
}

export function HotelDiningSection({ checkIn, checkOut, isHotelGuest }: HotelDiningSectionProps) {
  const { t } = useTranslation();
  const { data: diningData } = useDiningData();
  const restaurants: Restaurant[] = ((diningData?.data as unknown as { restaurants?: Restaurant[] })?.restaurants) ?? [];
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  return (
    <>
      <div id="dining-section" className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-5 h-5 text-hms-gold" />
            <h2 id="dining-section-title" className="text-lg font-semibold text-hms-text">{t('dining.diningExperiences')}</h2>
          </div>
          <Badge variant="outline">{restaurants.length} {t('dining.restaurants')}</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {restaurants.map((rest) => (
            <div
              key={rest.id}
              id={`dining-card-${rest.id}`}
              className="rounded-xl border border-hms-border bg-hms-card overflow-hidden hover:border-hms-gold/30 transition-colors group"
            >
              <RestaurantIllustration type={rest.type} className="w-full h-[140px]" />
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-[14px] font-semibold text-hms-text group-hover:text-hms-gold transition-colors">
                      {rest.name}
                    </h3>
                    <p className="text-[11px] text-hms-textDim mt-0.5">{rest.cuisine}</p>
                  </div>
                  <span className="text-[12px] text-hms-gold font-semibold">{PRICE_LABELS[rest.priceRange]}</span>
                </div>

                <p className="text-[12px] text-hms-textSub leading-relaxed line-clamp-2">{rest.description}</p>

                <div className="flex items-center gap-3 text-[11px] text-hms-textDim">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-hms-gold" /> {rest.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {rest.openTime} - {rest.closeTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" /> {rest.capacity}
                  </span>
                </div>

                {/* Seating + Dress code + Dietary tags */}
                <div className="flex flex-wrap gap-1.5">
                  {rest.dressCode && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-hms-gold/5 border border-hms-gold/15 text-hms-gold font-medium">
                      <Shirt className="w-2.5 h-2.5" /> {rest.dressCode}
                    </span>
                  )}
                  {rest.seatingOptions.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-hms-surface border border-hms-border/50 text-hms-textDim"
                    >
                      <Armchair className="w-2.5 h-2.5" /> {s}
                    </span>
                  ))}
                  {rest.dietaryTags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] px-2 py-0.5 rounded-md bg-hms-teal/5 border border-hms-teal/15 text-hms-teal"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Availability */}
                <div className="flex items-center justify-between">
                  <AvailabilityIndicator capacity={rest.capacity} />
                </div>

                <button
                  type="button"
                  id={`dining-reserve-${rest.id}`}
                  onClick={() => setSelectedRestaurant(rest)}
                  className="w-full h-9 rounded-lg bg-hms-gold/10 border border-hms-gold/20 text-[12px] font-semibold text-hms-gold hover:bg-hms-gold/20 transition-colors"
                >
                  {t('dining.reserveTable')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedRestaurant && (
        <DiningReservationDrawer
          restaurant={selectedRestaurant}
          checkIn={checkIn}
          checkOut={checkOut}
          isHotelGuest={isHotelGuest}
          onClose={() => setSelectedRestaurant(null)}
        />
      )}
    </>
  );
}
